import React from 'react';
import classNames from 'classnames';
import { Switch, Typography, TypographyType, TypographyVariant } from 'src/components-dummy';
import { Slider } from 'src/components-dummy/Slider';
import { GetWizardEstimateResult } from 'src/services';
import { useStepControlsSync } from '../useStepControlsSync';
import { useTrafficStep } from './useTrafficStep';
import { MdeOption, TrafficStepProps } from './types';
import { CUSTOM_PRODUCTION_STEP, TRAFFIC_PRESETS } from './constants';
import './TrafficStep.scss';

const ARM_ROWS: {
  key: 'production' | 'control' | 'variant';
  label: string;
  description: string;
}[] = [
  {
    key: 'production',
    label: 'Production',
    description: 'Your live, editable config. Keeps serving; not part of the verdict.',
  },
  {
    key: 'control',
    label: 'Control A',
    description: 'Frozen copy of today’s config — the baseline.',
  },
  {
    key: 'variant',
    label: 'Variant B',
    description: 'The lever change.',
  },
];

function renderDurationEstimate(
  selectedEstimate: GetWizardEstimateResult | undefined,
  isLoadingEstimates: boolean,
  selectedMde: number
): JSX.Element | null {
  if (isLoadingEstimates) {
    return <div className='syte-traffic-step-skeleton' />;
  }
  if (selectedEstimate?.status === 'ok') {
    return (
      <>
        <Typography type={TypographyType.Heading} variant={TypographyVariant.LargeBold}>
          {selectedEstimate.estimatedDays} days
        </Typography>
        <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
          to detect a {selectedMde}% change at {Math.round(selectedEstimate.confidenceLevel * 100)}%
          confidence, at {selectedEstimate.dailyTestUsers.toLocaleString()} test users/day. We
          won&apos;t call a winner before then.
        </Typography>
      </>
    );
  }
  if (selectedEstimate?.status === 'unavailable') {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        Estimate unavailable: {selectedEstimate.reason}
      </Typography>
    );
  }
  return null;
}

function getMdeChipLabel(option: MdeOption, isLoadingEstimates: boolean): string {
  if (isLoadingEstimates) {
    return '…';
  }
  if (option.estimate?.status === 'ok') {
    return `${option.mde}% · ${option.estimate.estimatedDays} days`;
  }
  return `${option.mde}% · —`;
}

export const TrafficStep = (props: TrafficStepProps): JSX.Element => {
  const { onStepControlsChange } = props;
  const {
    split,
    presetId,
    selectPreset,
    customProduction,
    setCustomProduction,
    recommendedPresetId,
    mdeOptions,
    selectedMde,
    setSelectedMde,
    isLoadingEstimates,
    autoStopOnSevereDrop,
    setAutoStopOnSevereDrop,
    emailVerdict,
    canContinue,
    isSubmitting,
    onSubmit,
  } = useTrafficStep(props);

  useStepControlsSync({ canContinue, isSubmitting, onSubmit }, onStepControlsChange, [
    canContinue,
    isSubmitting,
    onSubmit,
  ]);

  const selectedEstimate = mdeOptions.find(option => option.mde === selectedMde)?.estimate;

  return (
    <div className='syte-traffic-step'>
      <Typography type={TypographyType.Heading} variant={TypographyVariant.MediumBold}>
        Who sees the test?
      </Typography>
      <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
        Pick a split — control and variant always get an equal share, so the verdict is never skewed
        by traffic.
      </Typography>

      <div className='syte-traffic-step-card'>
        <div className='syte-traffic-step-presets'>
          {TRAFFIC_PRESETS.map(preset => (
            <button
              key={preset.id}
              type='button'
              className={classNames('syte-traffic-step-preset-card', {
                'syte-traffic-step-preset-card-selected': presetId === preset.id,
              })}
              onClick={() => selectPreset(preset.id)}
            >
              <div className='syte-traffic-step-preset-card-header'>
                <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                  {preset.label}
                </Typography>
                {preset.id === recommendedPresetId && (
                  <span className='syte-traffic-step-preset-card-badge'>Recommended</span>
                )}
              </div>
              <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                {preset.description}
              </Typography>
              <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
                {preset.split.production} / {preset.split.control} / {preset.split.variant}
              </Typography>
            </button>
          ))}
          <button
            type='button'
            className={classNames('syte-traffic-step-preset-card', {
              'syte-traffic-step-preset-card-selected': presetId === 'custom',
            })}
            onClick={() => setCustomProduction(customProduction)}
          >
            <div className='syte-traffic-step-preset-card-header'>
              <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                Custom
              </Typography>
            </div>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Set the production share — control and variant split the rest evenly.
            </Typography>
          </button>
        </div>

        {presetId === 'custom' && (
          <div className='syte-traffic-step-custom'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              Production share
            </Typography>
            <Slider
              value={customProduction}
              min={0}
              max={100}
              step={CUSTOM_PRODUCTION_STEP}
              onChange={(_event, value) =>
                setCustomProduction(Array.isArray(value) ? value[0] : value)
              }
            />
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              {customProduction}%
            </Typography>
          </div>
        )}

        <div className='syte-traffic-step-bar'>
          <div className='syte-traffic-step-bar-segment' style={{ width: `${split.production}%` }}>
            {split.production > 0 && `${split.production}%`}
          </div>
          <div
            className='syte-traffic-step-bar-segment syte-traffic-step-bar-segment-control'
            style={{ width: `${split.control}%` }}
          >
            {split.control > 0 && `${split.control}%`}
          </div>
          <div
            className='syte-traffic-step-bar-segment syte-traffic-step-bar-segment-variant'
            style={{ width: `${split.variant}%` }}
          >
            {split.variant > 0 && `${split.variant}%`}
          </div>
        </div>

        {ARM_ROWS.map(row => (
          <div key={row.key} className='syte-traffic-step-row'>
            <div className='syte-traffic-step-row-label'>
              <span
                className={classNames(
                  'syte-traffic-step-row-dot',
                  `syte-traffic-step-row-dot-${row.key}`
                )}
              />
              <div>
                <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
                  {row.label}
                </Typography>
                <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                  {row.description}
                </Typography>
              </div>
            </div>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              {split[row.key]}%
            </Typography>
          </div>
        ))}
      </div>

      <div className='syte-traffic-step-columns'>
        <div className='syte-traffic-step-card'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
            HOW LONG UNTIL WE KNOW
          </Typography>
          {renderDurationEstimate(selectedEstimate, isLoadingEstimates, selectedMde)}

          <div className='syte-traffic-step-mde'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
              SMALLEST CHANGE WORTH ACTING ON
            </Typography>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Smaller changes need more shoppers to prove — and take longer.
            </Typography>
            <div className='syte-traffic-step-mde-chips'>
              {mdeOptions.map(option => (
                <button
                  key={option.mde}
                  type='button'
                  className={classNames('syte-traffic-step-mde-chip', {
                    'syte-traffic-step-mde-chip-selected': selectedMde === option.mde,
                  })}
                  onClick={() => setSelectedMde(option.mde)}
                >
                  {getMdeChipLabel(option, isLoadingEstimates)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className='syte-traffic-step-card'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
            GUARDRAILS
          </Typography>
          <div className='syte-traffic-step-guardrail-row'>
            <Switch
              checked={autoStopOnSevereDrop}
              onChange={checked => setAutoStopOnSevereDrop(checked)}
            >
              <Switch.TitleTemplate>Auto-stop on severe drop</Switch.TitleTemplate>
            </Switch>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              End the test automatically if the deciding metric drops more than 10% at 95%
              confidence.
            </Typography>
          </div>
          <div className='syte-traffic-step-guardrail-row'>
            <Switch checked={emailVerdict} disabled onChange={() => {}}>
              <Switch.TitleTemplate>
                Email me the verdict{' '}
                <span className='syte-traffic-step-guardrail-badge'>Coming soon</span>
              </Switch.TitleTemplate>
            </Switch>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Notify when the experiment reaches significance or its end date.
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
